import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SwanEmploymentStatusSchema, SwanMonthlyIncomeSchema } from '@clari/shared'
import { Button, Card, Input, Skeleton } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { useResidenceEligibility } from '@/hooks/useResidenceEligibility'
import { apiErrorMessage } from '@/lib/api-error'
import { bankingApi } from '@/lib/banking-api'

const EMPLOYMENT_OPTIONS = SwanEmploymentStatusSchema.options
const INCOME_OPTIONS = SwanMonthlyIncomeSchema.options

const selectClass =
  'w-full rounded-xl border border-border bg-bg-elevated px-3 py-2 text-text focus:border-border-focus focus:outline-none'

/**
 * KYC fields Swan requires before we can open an account (étape 12). On submit we
 * create the onboarding server-side, then redirect to Swan's hosted identity flow.
 */
export function AccountOnboarding() {
  const { t } = useTranslation('banking')
  const navigate = useNavigate()
  // Eligibility decided on a FRESH /me (not the stale persisted store user) —
  // see useResidenceEligibility. We don't render the form until it resolves.
  const { loading: eligibilityLoading, eligible } = useResidenceEligibility()
  const [employmentStatus, setEmploymentStatus] = useState('')
  const [monthlyIncome, setMonthlyIncome] = useState('')
  const [isUsPerson, setIsUsPerson] = useState<boolean | null>(null)
  const [usTaxId, setUsTaxId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    if (!employmentStatus || !monthlyIncome || isUsPerson === null) {
      setError(t('onboarding.form.incomplete'))
      return
    }
    // Swan requires a 9-digit US TIN for US persons.
    if (isUsPerson === true && !/^\d{9}$/.test(usTaxId.trim())) {
      setError(t('onboarding.form.us_tax_id_invalid'))
      return
    }
    setLoading(true)
    try {
      const res = await bankingApi.startOnboarding({
        employment_status: employmentStatus,
        monthly_income: monthlyIncome,
        is_united_states_person: isUsPerson,
        ...(isUsPerson ? { us_tax_id: usTaxId.trim() } : {}),
      })
      // Safety net: backend agrees on eligibility (front already gated below).
      if (!res.eligible) {
        setError(t('onboarding.ineligible_body'))
        setLoading(false)
        return
      }
      // Full-page redirect to Swan's hosted onboarding (login + idverified + consent).
      window.location.assign(res.consent_url)
    } catch (err) {
      setError(apiErrorMessage(err))
      setLoading(false)
    }
  }

  if (eligibilityLoading) {
    return (
      <div className="mx-auto max-w-xl">
        <Card>
          <Skeleton className="h-7 w-1/2" />
          <Skeleton className="mt-3 h-4 w-full" />
          <Skeleton className="mt-2 h-4 w-2/3" />
        </Card>
      </div>
    )
  }

  if (!eligible) {
    return (
      <div className="mx-auto max-w-xl">
        <Card>
          <h1 className="text-2xl font-semibold">{t('onboarding.ineligible_title')}</h1>
          <p className="mt-2 text-text-muted">{t('onboarding.ineligible_body')}</p>
          <Button className="mt-6" variant="secondary" onClick={() => navigate('/app')}>
            {t('onboarding.form.cancel')}
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-xl">
      <Card>
        <h1 className="text-2xl font-semibold">{t('onboarding.form.title')}</h1>
        <p className="mt-2 text-text-muted">{t('onboarding.form.subtitle')}</p>

        {error ? <div className="mt-4"><ErrorBanner message={error} /></div> : null}

        <form className="mt-6 flex flex-col gap-5" onSubmit={onSubmit}>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-text-muted">{t('onboarding.form.employment_label')}</span>
            <select
              className={selectClass}
              value={employmentStatus}
              onChange={(e) => setEmploymentStatus(e.target.value)}
            >
              <option value="" disabled>
                {t('onboarding.form.select_placeholder')}
              </option>
              {EMPLOYMENT_OPTIONS.map((v) => (
                <option key={v} value={v}>
                  {t(`onboarding.employment.${v}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-text-muted">{t('onboarding.form.income_label')}</span>
            <select
              className={selectClass}
              value={monthlyIncome}
              onChange={(e) => setMonthlyIncome(e.target.value)}
            >
              <option value="" disabled>
                {t('onboarding.form.select_placeholder')}
              </option>
              {INCOME_OPTIONS.map((v) => (
                <option key={v} value={v}>
                  {t(`onboarding.income.${v}`)}
                </option>
              ))}
            </select>
          </label>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm text-text-muted">{t('onboarding.form.fatca_label')}</legend>
            <div className="flex gap-3">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="fatca"
                  checked={isUsPerson === false}
                  onChange={() => setIsUsPerson(false)}
                />
                <span>{t('onboarding.form.fatca_no')}</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="fatca"
                  checked={isUsPerson === true}
                  onChange={() => setIsUsPerson(true)}
                />
                <span>{t('onboarding.form.fatca_yes')}</span>
              </label>
            </div>
          </fieldset>

          {isUsPerson === true ? (
            <Input
              label={t('onboarding.form.us_tax_id_label')}
              value={usTaxId}
              onChange={(e) => setUsTaxId(e.target.value)}
              placeholder={t('onboarding.form.us_tax_id_placeholder')}
            />
          ) : null}

          <div className="mt-2 flex gap-2">
            <Button type="submit" disabled={loading} withSignatureDot>
              {loading ? t('onboarding.form.submitting') : t('onboarding.form.submit')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/app')}>
              {t('onboarding.form.cancel')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
